import type { MessageKey } from '@dijitalapp/i18n';
import type {
  AssignmentDto,
  ClassStudentDto,
  ClassSummaryDto,
  LearnOverviewDto,
  SchoolDto,
  SchoolPersonDto,
  SubmissionRowDto,
  SubmissionStatus,
} from '@dijitalapp/shared';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { PageEmpty, PageHeader } from '../components/SectionNav';
import { GuardianQuizList, StudentQuizzes, TeacherQuizzes } from '../components/QuizUi';
import { GuardianStudySummary, StudyPlan } from '../components/StudyPlan';
import { GuardianTutorControl, StudentTutor, TeacherTutorSwitch } from '../components/TutorUi';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { useChat } from '../lib/chat-context';
import { useShell } from './Shell';
import { DateInput } from '../components/DateInput';

/** The shareable URL: the token rides in the fragment, which browsers never send to servers. */
const eduJoinUrl = (token: string) => `${location.origin}/edu-join#${token}`;

function useRun() {
  const { i18n } = useApp();
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      setError(null);
      try {
        await fn();
        return true;
      } catch (err) {
        setError(i18n.t(errorMessageKey(err)));
        return false;
      }
    },
    [i18n],
  );
  return { error, run };
}

function ErrorLine({ error }: { error: string | null }) {
  return error ? (
    <p className="alert alert-error" role="alert">
      {error}
    </p>
  ) : null;
}

/** A one-time link shown once with a copy button (only hashes are stored on the server). */
function LinkBox({ token, id }: { token: string; id: string }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const [copied, setCopied] = useState(false);
  return (
    <div className="stack" style={{ gap: 'var(--space-2)' }}>
      <div className="row" style={{ flexWrap: 'nowrap' }}>
        <label className="visually-hidden" htmlFor={id}>
          {t('learn.link')}
        </label>
        <input id={id} className="input" readOnly value={eduJoinUrl(token)} onFocus={(e) => e.currentTarget.select()} />
        <button className="btn" type="button" onClick={() => void navigator.clipboard.writeText(eduJoinUrl(token)).then(() => setCopied(true), () => undefined)}>
          {copied ? t('group.linkCopied') : t('group.copyLink')}
        </button>
      </div>
      <p className="hint">{t('group.linkOnce')}</p>
    </div>
  );
}

const STATUS_KEY: Record<SubmissionStatus, MessageKey> = {
  not_submitted: 'learn.status.not_submitted',
  missing: 'learn.status.missing',
  submitted: 'learn.status.submitted',
  returned: 'learn.status.returned',
};

/** `/learn`: schools, classes and homework; children for guardians; school requests for adults. */
export function LearnPage() {
  const { i18n, me } = useApp();
  const { t } = i18n;
  const chat = useChat();
  const [data, setData] = useState<LearnOverviewDto | null>(null);
  const { section, classId } = useParams();
  const navigate = useNavigate();
  const { setMobileView } = useShell();
  const { error, run } = useRun();

  const load = useCallback(() => api<LearnOverviewDto>('GET', '/learn').then(setData), []);
  useEffect(() => {
    void run(load);
  }, [load, run, chat.learnVersion]);

  // Sections come from the URL; the middle pane lists them (overview, study plan, classes, add a school).
  const current = classId ? (data?.classes.find((c) => c.id === classId) ?? null) : null;
  const back = () => setMobileView('list');

  if (data && current) {
    return (
      <div className="content-inner">
        <ClassView cls={current} onBack={back} />
      </div>
    );
  }
  if (section === 'plan') {
    return (
      <div className="content-inner">
        <PageHeader title={t('study.title')} description={t('learn.nav.planDesc')} back={back} />
        <StudyPlan />
      </div>
    );
  }
  if (section === 'school' && data && me?.ageBand === 'adult') {
    return (
      <div className="content-inner">
        <PageHeader title={t('learn.request.title')} description={t('learn.nav.schoolDesc')} back={back} />
        <SchoolRequestForm requests={data.requests} onDone={() => void run(load)} />
      </div>
    );
  }

  const nothing = data && data.classes.length === 0 && data.children.length === 0 && data.schools.length === 0;
  return (
    <div className="content-inner">
      <PageHeader title={t('learn.title')} description={t('learn.nav.homeDesc')} back={back} />
      <ErrorLine error={error} />
      {!data && !error && <p className="muted">{t('app.loading')}</p>}
      {nothing && (
        <PageEmpty
          icon="learn"
          title={t('learn.emptyTitle')}
          body={t('learn.empty')}
          action={
            me?.ageBand === 'adult' ? (
              <button className="btn" type="button" onClick={() => navigate('/learn/school')}>
                {t('learn.request.title')}
              </button>
            ) : undefined
          }
        />
      )}
      {data && !nothing && (
        <>
          {data.classes.length > 0 && (
            <section className="card stack" aria-labelledby="learn-classes">
              <h2 id="learn-classes" className="card-title">
                {t('learn.classes')}
              </h2>
              {data.classes.map((c) => (
                <button key={c.id} type="button" className="list-item list-item-card" onClick={() => navigate(`/learn/class/${c.id}`)}>
                  <span className="avatar" aria-hidden>
                    {Array.from(c.name)[0]?.toUpperCase()}
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <strong>{c.name}</strong>
                    {c.subject && <span className="muted"> · {c.subject}</span>}
                    <br />
                    <span className="muted">
                      {c.schoolName} · {t('learn.teachers', { names: c.teachers.join(', ') })} · {t('learn.studentCount', { count: c.studentCount })}
                    </span>
                  </span>
                  <span className="spacer" />
                  <span className="badge">{t(c.myRole === 'teacher' ? 'learn.role.teacher' : 'learn.role.student')}</span>
                </button>
              ))}
            </section>
          )}

          {data.children.map((child) => (
            <section key={`${child.student.id}:${child.school.id}`} className="card stack">
              <h2 className="card-title">{t('learn.childTitle', { name: child.student.displayName })}</h2>
              <p className="muted">
                {child.school.name} · {child.classes.map((c) => c.name).join(', ') || t('learn.noClasses')}
              </p>
              {child.assignments.length === 0 && <p className="muted">{t('learn.noAssignments')}</p>}
              {child.assignments.map((a) => (
                <div key={a.id} className="toggle-row">
                  <span style={{ minWidth: 0 }}>
                    <strong>{a.title}</strong> <span className="muted">· {a.className}</span>
                    {a.dueAt && <span className="muted"> · {t('planner.due', { date: i18n.formatDate(a.dueAt) })}</span>}
                    {a.feedback && (
                      <>
                        <br />
                        <span className="muted">{t('learn.feedbackLine', { text: a.feedback })}</span>
                      </>
                    )}
                  </span>
                  <span className="spacer" />
                  {a.grade !== null && <span className="badge">{a.grade}/100</span>}
                  <span className={`badge ${a.status === 'missing' ? 'badge-danger' : ''}`}>{t(STATUS_KEY[a.status])}</span>
                  {a.late && <span className="badge">{t('learn.late')}</span>}
                </div>
              ))}
              <GuardianQuizList quizzes={child.quizzes} />
              <GuardianStudySummary summary={child.studyPlan} />
              <GuardianTutorControl studentId={child.student.id} initial={child.tutor} />
              <p className="hint">{t('learn.guardianNote')}</p>
            </section>
          ))}

          {data.guardians.length > 0 && (
            <section className="card stack">
              <h2 className="card-title">{t('learn.myGuardians')}</h2>
              {data.guardians.map((g) => (
                <p key={g.id + g.schoolName}>
                  {g.displayName} <span className="muted">· {g.schoolName}</span>
                </p>
              ))}
              <p className="hint">{t('learn.myGuardiansNote')}</p>
            </section>
          )}

          {data.schools.map((s) => (
            <SchoolPanel key={s.id} school={s} onChanged={() => void run(load)} />
          ))}

        </>
      )}
    </div>
  );
}

function SchoolRequestForm({ requests, onDone }: { requests: LearnOverviewDto['requests']; onDone: () => void }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const { error, run } = useRun();
  const [form, setForm] = useState({ schoolName: '', city: '', note: '' });
  const pending = requests.some((r) => r.status === 'pending');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await api('POST', '/schools/requests', { schoolName: form.schoolName, city: form.city, note: form.note || undefined });
      setForm({ schoolName: '', city: '', note: '' });
      onDone();
    });
  };
  return (
    <section className="card stack" aria-labelledby="school-request">
      <h2 id="school-request">{t('learn.request.title')}</h2>
      <p className="hint">{t('learn.request.note')}</p>
      {requests.map((r) => (
        <p key={r.id}>
          <strong>{r.schoolName}</strong> <span className="muted">· {r.city} · </span>
          <span className="badge">{t(`learn.request.${r.status}` as MessageKey)}</span>
          {r.reviewNote && <span className="muted"> · {r.reviewNote}</span>}
        </p>
      ))}
      <ErrorLine error={error} />
      {!pending && (
        <form className="stack" onSubmit={submit}>
          <div className="row">
            <div className="field">
              <label htmlFor="sr-name">{t('learn.request.schoolName')}</label>
              <input id="sr-name" className="input" required minLength={2} maxLength={120} value={form.schoolName} onChange={(e) => setForm({ ...form, schoolName: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="sr-city">{t('learn.request.city')}</label>
              <input id="sr-city" className="input" required minLength={2} maxLength={80} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
          </div>
          <div className="field">
            <label htmlFor="sr-note">{t('learn.request.message')}</label>
            <textarea id="sr-note" className="input" rows={2} maxLength={1000} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>
          <div className="row">
            <span className="spacer" />
            <button className="btn btn-primary">{t('learn.request.submit')}</button>
          </div>
        </form>
      )}
    </section>
  );
}

/** School admin tools (people, teacher and guardian invites) and class creation for teachers. */
function SchoolPanel({ school, onChanged }: { school: SchoolDto; onChanged: () => void }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const { error, run } = useRun();
  const admin = school.myRole === 'owner' || school.myRole === 'admin';
  const canCreateClass = admin || school.myEduRole === 'teacher';
  const [people, setPeople] = useState<SchoolPersonDto[]>([]);
  const [token, setToken] = useState<{ kind: string; value: string } | null>(null);
  const [studentId, setStudentId] = useState('');
  const [cls, setCls] = useState({ name: '', subject: '' });

  useEffect(() => {
    if (!admin) return;
    void api<{ people: SchoolPersonDto[] }>('GET', `/schools/${school.id}/people`).then((r) => setPeople(r.people), () => undefined);
  }, [admin, school.id]);

  if (!admin && !canCreateClass) {
    return (
      <section className="card">
        <strong>{school.name}</strong>{' '}
        <span className="muted">· {school.myEduRole ? t(`learn.role.${school.myEduRole}` as MessageKey) : t(`spaces.role.${school.myRole}` as MessageKey)}</span>
      </section>
    );
  }

  const issue = (body: Record<string, unknown>, kind: string) =>
    run(async () => {
      const r = await api<{ token: string }>('POST', `/schools/${school.id}/invites`, body);
      setToken({ kind, value: r.token });
    });
  const createClass = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await api('POST', `/schools/${school.id}/classes`, { name: cls.name, subject: cls.subject || undefined });
      setCls({ name: '', subject: '' });
      onChanged();
    });
  };
  const students = people.filter((p) => p.eduRole === 'student');

  return (
    <section className="card stack" aria-label={school.name}>
      <h2>
        {school.name} <span className="badge">✓ {t('learn.verified')}</span>
      </h2>
      <ErrorLine error={error} />
      {admin && (
        <>
          <h3>{t('learn.people')}</h3>
          <p className="muted">
            {(['teacher', 'student', 'guardian'] as const).map((r) => `${t(`learn.role.${r}` as MessageKey)}: ${people.filter((p) => p.eduRole === r).length}`).join(' · ')}
          </p>
          <div className="row">
            <button className="btn" type="button" onClick={() => void issue({ kind: 'teacher' }, 'teacher')}>
              {t('learn.inviteTeacher')}
            </button>
            <label className="visually-hidden" htmlFor={`gs-${school.id}`}>
              {t('learn.pickStudent')}
            </label>
            <select id={`gs-${school.id}`} className="input" style={{ width: 'auto' }} value={studentId} onChange={(e) => setStudentId(e.target.value)}>
              <option value="">{t('learn.pickStudent')}</option>
              {students.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.displayName} (@{s.username})
                </option>
              ))}
            </select>
            <button className="btn" type="button" disabled={!studentId} onClick={() => void issue({ kind: 'guardian', studentId }, 'guardian')}>
              {t('learn.inviteGuardian')}
            </button>
          </div>
          {token && (
            <>
              <p className="hint">{t(token.kind === 'teacher' ? 'learn.teacherLinkNote' : 'learn.guardianLinkNote')}</p>
              <LinkBox token={token.value} id={`sl-${school.id}`} />
            </>
          )}
        </>
      )}
      {canCreateClass && (
        <form className="row" onSubmit={createClass}>
          <div className="field">
            <label htmlFor={`cn-${school.id}`}>{t('learn.className')}</label>
            <input id={`cn-${school.id}`} className="input" required maxLength={80} value={cls.name} onChange={(e) => setCls({ ...cls, name: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor={`cs-${school.id}`}>{t('learn.subject')}</label>
            <input id={`cs-${school.id}`} className="input" maxLength={80} value={cls.subject} onChange={(e) => setCls({ ...cls, subject: e.target.value })} />
          </div>
          <button className="btn btn-primary">{t('learn.createClass')}</button>
        </form>
      )}
    </section>
  );
}

function ClassView({ cls, onBack }: { cls: ClassSummaryDto; onBack: () => void }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const chat = useChat();
  const teacher = cls.myRole === 'teacher';
  const { error, run } = useRun();
  const [assignments, setAssignments] = useState<AssignmentDto[] | null>(null);
  const [students, setStudents] = useState<ClassStudentDto[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const [form, setForm] = useState({ title: '', instructions: '', due: '' });

  const load = useCallback(async () => {
    setAssignments((await api<{ assignments: AssignmentDto[] }>('GET', `/classes/${cls.id}/assignments`)).assignments);
    if (teacher) setStudents((await api<{ students: ClassStudentDto[] }>('GET', `/classes/${cls.id}/students`)).students);
  }, [cls.id, teacher]);
  useEffect(() => {
    void run(load);
  }, [load, run, chat.learnVersion]);

  const create = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await api('POST', `/classes/${cls.id}/assignments`, {
        title: form.title,
        instructions: form.instructions || undefined,
        dueAt: form.due ? new Date(`${form.due}T23:59`).toISOString() : null,
      });
      setForm({ title: '', instructions: '', due: '' });
      await load();
    });
  };

  return (
    <>
      <PageHeader
        title={cls.name}
        description={[cls.subject, cls.schoolName, t(cls.myRole === 'teacher' ? 'learn.role.teacher' : 'learn.role.student')].filter(Boolean).join(' · ')}
        back={onBack}
      />
      <ErrorLine error={error} />

      {teacher && (
        <section className="card stack">
          <h3>{t('learn.studentsTitle', { count: students.length })}</h3>
          <p className="muted">{students.map((s) => s.displayName).join(', ') || t('learn.noStudents')}</p>
          <div className="row">
            <button className="btn" type="button" onClick={() => void run(async () => setToken((await api<{ token: string }>('POST', `/classes/${cls.id}/invite-link`, {})).token))}>
              {t('learn.classLink')}
            </button>
            <button className="btn btn-danger" type="button" onClick={() => void run(async () => (await api('DELETE', `/classes/${cls.id}/invite-link`), setToken(null)))}>
              {t('group.revokeLink')}
            </button>
          </div>
          {token && (
            <>
              <p className="hint">{t('learn.classLinkNote')}</p>
              <LinkBox token={token} id={`cl-${cls.id}`} />
            </>
          )}
        </section>
      )}

      {teacher && (
        <form className="card stack" onSubmit={create}>
          <h3>{t('learn.newAssignment')}</h3>
          <div className="field">
            <label htmlFor="as-title">{t('learn.assignmentTitle')}</label>
            <input id="as-title" className="input" required maxLength={200} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="as-ins">{t('learn.instructions')}</label>
            <textarea id="as-ins" className="input" rows={3} maxLength={5000} value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} />
          </div>
          <div className="row">
            <div className="field">
              <label htmlFor="as-due">{t('planner.dueDate')}</label>
              <DateInput id="as-due" value={form.due} onChange={(due) => setForm((f) => ({ ...f, due }))} />
            </div>
            <span className="spacer" />
            <button className="btn btn-primary">{t('learn.assign')}</button>
          </div>
        </form>
      )}

      {teacher ? <TeacherTutorSwitch classId={cls.id} /> : <StudentTutor classId={cls.id} />}

      {teacher ? <TeacherQuizzes classId={cls.id} /> : <StudentQuizzes classId={cls.id} />}

      <section className="card stack" aria-labelledby="as-list">
        <h3 id="as-list">{t('learn.assignments')}</h3>
        {assignments === null && <p className="muted">{t('app.loading')}</p>}
        {assignments?.length === 0 && <p className="muted">{t('learn.noAssignments')}</p>}
        {assignments?.map((a) => (teacher ? <TeacherAssignment key={a.id} a={a} onChanged={() => void run(load)} /> : <StudentAssignment key={a.id} a={a} onChanged={() => void run(load)} />))}
      </section>

      {!teacher && (
        <button
          className="btn btn-danger"
          type="button"
          onClick={() => {
            if (window.confirm(t('learn.leaveConfirm'))) void run(async () => (await api('DELETE', `/classes/${cls.id}/members/me`), onBack()));
          }}
        >
          {t('learn.leaveClass')}
        </button>
      )}
    </>
  );
}

function AssignmentHeader({ a }: { a: AssignmentDto }) {
  const { i18n } = useApp();
  const { t } = i18n;
  return (
    <>
      <strong>{a.title}</strong>
      {a.dueAt && <span className="muted"> · {t('planner.due', { date: i18n.formatDate(a.dueAt) })}</span>}
      {a.instructions && <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{a.instructions}</p>}
    </>
  );
}

function StudentAssignment({ a, onChanged }: { a: AssignmentDto; onChanged: () => void }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const { error, run } = useRun();
  const [body, setBody] = useState(a.mySubmission?.body ?? '');
  const sub = a.mySubmission;
  const locked = a.myStatus === 'returned';
  return (
    <div className="stack" style={{ borderTop: '1px solid var(--border)', paddingTop: 'var(--space-3)' }}>
      <div className="row">
        <span style={{ minWidth: 0 }}>
          <AssignmentHeader a={a} />
        </span>
        <span className="spacer" />
        {a.myStatus && <span className="badge">{t(STATUS_KEY[a.myStatus])}</span>}
        {sub?.late && <span className="badge">{t('learn.late')}</span>}
      </div>
      {locked && sub ? (
        <>
          <p style={{ whiteSpace: 'pre-wrap', margin: 0 }} className="muted">
            {sub.body}
          </p>
          <p>
            {sub.grade !== null && <strong>{sub.grade}/100 </strong>}
            {sub.feedback && t('learn.feedbackLine', { text: sub.feedback })}
          </p>
        </>
      ) : (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            void run(async () => (await api('PUT', `/assignments/${a.id}/submission`, { body }), onChanged()));
          }}
        >
          <label className="visually-hidden" htmlFor={`sub-${a.id}`}>
            {t('learn.yourAnswer')}
          </label>
          <textarea id={`sub-${a.id}`} className="input" rows={3} required maxLength={10000} placeholder={t('learn.yourAnswer')} value={body} onChange={(e) => setBody(e.target.value)} />
          <ErrorLine error={error} />
          <div className="row">
            <span className="hint">{t('learn.submitNote')}</span>
            <span className="spacer" />
            <button className="btn btn-primary">{sub ? t('learn.resubmit') : t('learn.submit')}</button>
          </div>
        </form>
      )}
    </div>
  );
}

function TeacherAssignment({ a, onChanged }: { a: AssignmentDto; onChanged: () => void }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const { error, run } = useRun();
  const [rows, setRows] = useState<SubmissionRowDto[] | null>(null);
  const [drafts, setDrafts] = useState<Record<string, { feedback: string; grade: string }>>({});
  const open = rows !== null;
  const load = () => run(async () => setRows((await api<{ submissions: SubmissionRowDto[] }>('GET', `/assignments/${a.id}/submissions`)).submissions));

  return (
    <div className="stack" style={{ borderTop: '1px solid var(--border)', paddingTop: 'var(--space-3)' }}>
      <div className="row">
        <span style={{ minWidth: 0 }}>
          <AssignmentHeader a={a} />
        </span>
        <span className="spacer" />
        {a.counts && <span className="badge">{t('learn.counts', { submitted: a.counts.submitted, students: a.counts.students, returned: a.counts.returned })}</span>}
        <button className="btn" type="button" aria-expanded={open} onClick={() => (open ? setRows(null) : void load())}>
          {t('learn.submissions')}
        </button>
        <button
          className="link-button"
          type="button"
          onClick={() => {
            if (window.confirm(t('learn.deleteAssignmentConfirm'))) void run(async () => (await api('DELETE', `/assignments/${a.id}`), onChanged()));
          }}
        >
          {t('planner.delete')}
        </button>
      </div>
      <ErrorLine error={error} />
      {rows?.map((r) => {
        const d = drafts[r.student.id] ?? { feedback: r.submission?.feedback ?? '', grade: r.submission?.grade?.toString() ?? '' };
        return (
          <div key={r.student.id} className="stack" style={{ paddingLeft: 'var(--space-3)' }}>
            <div className="row">
              <strong>{r.student.displayName}</strong>
              <span className={`badge ${r.status === 'missing' ? 'badge-danger' : ''}`}>{t(STATUS_KEY[r.status])}</span>
              {r.submission?.late && <span className="badge">{t('learn.late')}</span>}
            </div>
            {r.submission && (
              <>
                <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{r.submission.body}</p>
                <form
                  className="row"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void run(async () => {
                      await api('POST', `/assignments/${a.id}/submissions/${r.student.id}/return`, { feedback: d.feedback || undefined, grade: d.grade === '' ? null : Number(d.grade) });
                      await load();
                      onChanged();
                    });
                  }}
                >
                  <label className="visually-hidden" htmlFor={`fb-${a.id}-${r.student.id}`}>
                    {t('learn.feedback')}
                  </label>
                  <input
                    id={`fb-${a.id}-${r.student.id}`}
                    className="input"
                    style={{ flex: 1, minWidth: 160 }}
                    maxLength={5000}
                    placeholder={t('learn.feedback')}
                    value={d.feedback}
                    onChange={(e) => setDrafts({ ...drafts, [r.student.id]: { ...d, feedback: e.target.value } })}
                  />
                  <label className="visually-hidden" htmlFor={`gr-${a.id}-${r.student.id}`}>
                    {t('learn.grade')}
                  </label>
                  <input
                    id={`gr-${a.id}-${r.student.id}`}
                    className="input"
                    style={{ width: 90 }}
                    type="number"
                    min={0}
                    max={100}
                    placeholder={t('learn.grade')}
                    value={d.grade}
                    onChange={(e) => setDrafts({ ...drafts, [r.student.id]: { ...d, grade: e.target.value } })}
                  />
                  <button className="btn btn-primary">{r.status === 'returned' ? t('learn.updateReturn') : t('learn.return')}</button>
                </form>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
